import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints } from '@/lib/api/endpoints';
import { serviceAreaKeys } from '@/lib/queryKeys';

export interface CanadaRegionsCity {
  city: string;
  fsaCount: number;
  /**
   * Sorted, deduped list of every FSA covered by this city. The backend
   * surfaces it on every regions call so the picker can compute checkbox
   * state in-memory — no follow-up `/canada/expand` round-trip needed.
   */
  fsas: string[];
}

export interface CanadaRegionsProvince {
  code: string;
  name: string;
  fsaCount: number;
  cities: CanadaRegionsCity[];
}

export interface CanadaRegionsResponse {
  provinces: CanadaRegionsProvince[];
}

interface UseCanadaRegionsOptions {
  search?: string;
  province?: string;
  enabled?: boolean;
}

/**
 * Fetches the province / city tree powering the Canada region-picker.
 *
 * Backed by `GET /service-areas/canada/regions`. The server always returns
 * all 13 provinces (so the UI can show every card); the `cities` arrays
 * inside each province are filtered by `search` / `province` here on the
 * server so the FE never has to filter 1.6 k rows itself.
 */
export function useCanadaRegions({
  search,
  province,
  enabled = true,
}: UseCanadaRegionsOptions = {}) {
  const normalizedSearch = (search ?? '').trim().toLowerCase();
  const normalizedProvince = (province ?? '').trim().toUpperCase();

  return useApiQuery<CanadaRegionsResponse>({
    queryKey: [...serviceAreaKeys.all(), 'canada-regions', normalizedSearch, normalizedProvince],
    queryFn: () => {
      const params = new URLSearchParams();
      if (normalizedSearch) params.set('search', normalizedSearch);
      if (normalizedProvince) params.set('province', normalizedProvince);
      const qs = params.toString();
      const url = qs
        ? `${serviceAreaEndpoints.canadaRegions()}?${qs}`
        : serviceAreaEndpoints.canadaRegions();
      return apiGet<CanadaRegionsResponse>(operationsClient, url);
    },
    enabled,
    // The dataset is static — once we've loaded it, it doesn't change
    // until the operations container restarts. Keep it fresh for the
    // session and drop the refocus refetch.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
